/* GrowthOS — Import Opportunities modal (upload -> review -> done).
   Mirror of the leads/contacts/accounts import modal (same `gos-import-*` UI);
   wired to the app's real opportunity import (apiServices.importOpportunity +
   downloadFileCRM), reusing the exact success / invalid-file / row-error handling
   from the legacy importDeal.jsx page. The design's fabricated sample-row preview
   is replaced with an honest file-review step (no fake opportunity data in
   production). */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';
import { opportunityInputData, dropdownPayload } from '../../shared/constants';
import { fetchDataAndUpdateStore } from '../../redux/actions';

const PATHS = {
  upload: 'M12 21V9M7 14l5-5 5 5M5 3h14',
  x: 'M18 6 6 18M6 6l12 12',
  sheet: 'M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 12h8M8 16h8M8 12v6M12 12v6',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  check: 'M20 6 9 17l-5-5',
  chevL: 'M15 18l-6-6 6-6',
  chevD: 'M6 9l6 6 6-6',
};

const Ic = ({ name, size = 16, className }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const STEPS = [
  ['upload', 'Upload'],
  ['review', 'Review'],
  ['done', 'Done'],
];

// minimal, dependency-free CSV parser (handles quoted fields, "" escapes, CRLF)
function parseCsv(text) {
  const rows = [];
  let field = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\r') {
      // ignore
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => String(v).trim() !== ''));
}

const DealsImportModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [parsed, setParsed] = useState(null); // { headers, rows } for CSV preview
  const inputRef = useRef(null);
  const tplRef = useRef(null);

  // close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  // close the template dropdown on outside click
  useEffect(() => {
    if (!tplOpen) return undefined;
    const onDoc = (e) => {
      if (tplRef.current && !tplRef.current.contains(e.target)) setTplOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [tplOpen]);

  const accept = (f) => {
    if (!f) return;
    setFile(f);
    setParsed(null);
    setStep('review');
    if ((f.name || '').toLowerCase().endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const matrix = parseCsv(String(reader.result || ''));
          if (matrix.length) setParsed({ headers: matrix[0], rows: matrix.slice(1) });
        } catch (err) {
          /* leave preview empty; upload still works */
        }
      };
      reader.readAsText(f);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    accept(e.dataTransfer.files && e.dataTransfer.files[0]);
  };
  const downloadTemplate = (fmt) =>
    apiServices.downloadFileCRM(fmt === 'csv' ? 'OpportunitySampleTemplate.csv' : 'OpportunitySampleTemplate.xlsx', 'Opportunity');

  const finishSuccess = () => {
    toast.success('Opportunities imported successfully!', { position: 'top-right' });
    dispatch(fetchDataAndUpdateStore({ key: 'opportunityList', inputData: opportunityInputData }));
    dispatch(fetchDataAndUpdateStore({ key: 'opportunityDropdown', inputData: dropdownPayload }));
    setLoading(false);
    setStep('done');
  };

  const doImport = () => {
    if (!file || loading) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('OpportunityFile', file);
    const config = { headers: { 'content-type': 'multipart/form-data' } };

    apiServices
      .importOpportunity(formData, config)
      .then((response) => {
        if (response.status === 200 && response.data) {
          if (typeof response.data === 'object' && response.data.message === 'Import Opportunity Successful') {
            finishSuccess();
            return;
          }
          if (typeof response.data === 'object' && response.data.message === 'Invalid File') {
            toast.error('Invalid file format. Please upload a valid CSV.', { position: 'top-right' });
            setLoading(false);
            return;
          }
          // CSV string with possible per-row errors
          const csvText = response.data;
          const lines = String(csvText)
            .split('\n')
            .filter((line) => line.trim() !== '');
          if (lines.length === 0) {
            toast.error('No data returned.', { position: 'top-right' });
            setLoading(false);
            return;
          }
          const headers = lines[0].split(',').map((h) => h.replace(/"/g, '').trim());
          const errorsIndex = headers.indexOf('Errors');
          let hasErrors = false;
          if (errorsIndex !== -1) {
            for (let i = 1; i < lines.length; i++) {
              const row = lines[i].split(',');
              const errorValue = row[errorsIndex] ? row[errorsIndex].replace(/"/g, '').trim() : '';
              if (errorValue && errorValue.toLowerCase() !== 'null') {
                hasErrors = true;
                break;
              }
            }
          }
          if (hasErrors) {
            const blob = new Blob([csvText], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'opportunity_import_errors.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.error('Some rows have errors. Error file downloaded.', { position: 'top-right' });
            setLoading(false);
          } else {
            finishSuccess();
          }
        } else {
          toast.error('An unexpected error occurred', { position: 'top-right' });
          setLoading(false);
        }
      })
      .catch((error) => {
        const message = error.response?.data?.message;
        if (message === 'Invalid File') {
          toast.error('Invalid file format. Please upload a valid CSV.', { position: 'top-right' });
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach((field) => {
            errors[field].forEach((msg) => toast.error(msg, { position: 'top-right' }));
          });
        } else {
          toast.error('Something went wrong while uploading the file.', { position: 'top-right' });
        }
        setLoading(false);
      });
  };

  const curIdx = STEPS.findIndex((s) => s[0] === step);

  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="gos-import-modal" role="dialog" aria-modal="true" aria-label="Import opportunities">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="upload" size={16} /></span>
            <div>
              <div className="gos-import-title">Import Opportunities</div>
              <div className="gos-import-sub">
                {step === 'upload' ? 'Upload a CSV or Excel file' : step === 'review' ? 'Review your file' : 'Import complete'}
              </div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close" disabled={loading}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-import-stepper">
          {STEPS.map((s, i) => (
            <div key={s[0]} className={'gos-import-stepper-item' + (i <= curIdx ? ' active' : '')}>
              <span className="gos-import-stepper-dot">{i < curIdx ? <Ic name="check" size={12} /> : i + 1}</span>
              {s[1]}
              {i < STEPS.length - 1 ? <span className="gos-import-stepper-line" /> : null}
            </div>
          ))}
        </div>

        {step === 'upload' && (
          <div className="gos-import-body">
            <div
              className={'gos-import-drop' + (drag ? ' drag' : '')}
              role="button"
              tabIndex={0}
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current && inputRef.current.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current && inputRef.current.click();
                }
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => accept(e.target.files && e.target.files[0])}
              />
              <span className="gos-import-drop-ic"><Ic name="upload" size={26} /></span>
              <div className="gos-import-drop-title">Drag &amp; drop your file here</div>
              <div className="gos-import-drop-sub">
                or <span className="gos-import-link">browse</span> — CSV, XLSX up to 5000 records
              </div>
            </div>
            <div className="gos-import-template">
              <Ic name="sheet" size={18} className="gos-import-tpl-ic" />
              <div className="gos-import-grow">
                <div className="gos-import-tpl-title">Not sure about the format?</div>
                <div className="gos-import-sub">Download our sample template with the required columns.</div>
              </div>
              <div className="gos-import-tpl-wrap" ref={tplRef}>
                <button
                  type="button"
                  className={'gos-import-tpl-btn' + (tplOpen ? ' on' : '')}
                  onClick={() => setTplOpen((o) => !o)}
                  aria-expanded={tplOpen}
                >
                  <Ic name="download" size={14} /> Template <Ic name="chevD" size={13} />
                </button>
                {tplOpen && (
                  <div className="gos-import-tpl-menu">
                    <button
                      type="button"
                      className="gos-import-tpl-item"
                      onClick={() => {
                        downloadTemplate('csv');
                        setTplOpen(false);
                      }}
                    >
                      <span className="gos-import-tpl-badge csv">CSV</span>
                      <span className="gos-import-grow">
                        <span className="gos-import-tpl-name">CSV template</span>
                        <span className="gos-import-tpl-subt">Comma-separated · .csv</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="gos-import-tpl-item"
                      onClick={() => {
                        downloadTemplate('xlsx');
                        setTplOpen(false);
                      }}
                    >
                      <span className="gos-import-tpl-badge xls">XLS</span>
                      <span className="gos-import-grow">
                        <span className="gos-import-tpl-name">Excel template</span>
                        <span className="gos-import-tpl-subt">Opens in Excel · .xlsx</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="gos-import-body">
            <div className="gos-import-filechip">
              <Ic name="sheet" size={16} className="gos-import-tpl-ic" />
              <span className="gos-import-filename">{file?.name}</span>
              {parsed ? <span className="gos-import-rows-badge">{parsed.rows.length} rows</span> : null}
              <button type="button" className="gos-import-link gos-import-replace" onClick={() => setStep('upload')}>
                Replace
              </button>
            </div>
            {parsed && parsed.headers.length > 0 && (
              <div className="gos-import-table-wrap">
                <table className="gos-import-tbl">
                  <thead>
                    <tr>
                      {parsed.headers.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 100).map((r, ri) => (
                      <tr key={ri}>
                        {parsed.headers.map((_, ci) => (
                          <td key={ci}>{r[ci] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="gos-import-note">
              <Ic name="info" size={14} />
              <span>
                New rows will be added as <strong>new opportunities</strong> and assigned to you. Rows with errors
                are skipped and returned as a downloadable file.
              </span>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="gos-import-done">
            <div className="gos-import-done-ic"><Ic name="check" size={30} /></div>
            <div className="gos-import-done-title">Opportunities imported</div>
            <div className="gos-import-done-sub">Your file was processed and the opportunities list has been refreshed.</div>
          </div>
        )}

        {step === 'upload' && (
          <div className="gos-import-foot end">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        )}
        {step === 'review' && (
          <div className="gos-import-foot">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={() => setStep('upload')}>
              <Ic name="chevL" size={15} /> Back
            </button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={doImport} disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Importing…
                </>
              ) : (
                <>
                  <Ic name="check" size={16} /> {parsed ? `Import ${parsed.rows.length} opportunities` : 'Import opportunities'}
                </>
              )}
            </button>
          </div>
        )}
        {step === 'done' && (
          <div className="gos-import-foot end">
            <button type="button" className="gos-btn gos-btn-primary" onClick={onClose}>View opportunities</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealsImportModal;
